import { markerCenter, markerElementForIndex } from "./chart_geometry"

const POSITIONED_CLASS = "apex-chart-tooltip--positioned"

function placementRect(top, left, width, height) {
  return { left, top, right: left + width, bottom: top + height }
}

function rectsOverlap(a, b) {
  return !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom)
}

function rectInsidePlot(rect, plot) {
  return (
    rect.left >= plot.left &&
    rect.right <= plot.right &&
    rect.top >= plot.top &&
    rect.bottom <= plot.bottom
  )
}

function clampHorizontal(left, tooltipWidth, plotBounds) {
  return Math.max(plotBounds.left, Math.min(left, plotBounds.right - tooltipWidth))
}

function tooltipPositionRoot(tooltip, instance) {
  if (tooltip.offsetParent instanceof HTMLElement) return tooltip.offsetParent
  return instance.el?.querySelector(".apexcharts-canvas") || instance.el
}

function rectRelativeToRoot(element, rootRect) {
  const rect = element.getBoundingClientRect()
  return {
    left: rect.left - rootRect.left,
    right: rect.right - rootRect.left,
    top: rect.top - rootRect.top,
    bottom: rect.bottom - rootRect.top,
    width: rect.width,
    height: rect.height
  }
}

function lineHoverGeometry(instance, dataPointIndex, tooltip, options = {}) {
  const markerEl = markerElementForIndex(instance, dataPointIndex)
  const positionRoot = tooltipPositionRoot(tooltip, instance)
  if (!markerEl || !positionRoot) return null

  const rootRect = positionRoot.getBoundingClientRect()
  const markerBox = rectRelativeToRoot(markerEl, rootRect)

  let labelTop = markerBox.top - (options.dataLabelPad ?? 36)
  const labelRects = instance.el?.querySelectorAll(
    ".apexcharts-datalabels rect, .apexcharts-data-labels rect"
  )
  const labelRect = labelRects?.[dataPointIndex]
  if (labelRect) {
    const labelBox = rectRelativeToRoot(labelRect, rootRect)
    labelTop = Math.min(labelTop, labelBox.top - 4)
  }

  const inner = instance.el?.querySelector(".apexcharts-inner")
  const plotBox = inner
    ? rectRelativeToRoot(inner, rootRect)
    : {
      left: 0,
      right: rootRect.width,
      top: 0,
      bottom: rootRect.height
    }

  return {
    center: {
      x: markerBox.left + markerBox.width / 2,
      y: markerBox.top + markerBox.height / 2
    },
    keepOut: {
      left: markerBox.left - 8,
      right: markerBox.right + 8,
      top: labelTop,
      bottom: markerBox.bottom + 8
    },
    plot: {
      left: plotBox.left,
      right: plotBox.right,
      top: plotBox.top,
      bottom: plotBox.bottom
    }
  }
}

export function computeLineTooltipPlacement(tooltipWidth, tooltipHeight, geometry, options = {}) {
  const { center, keepOut, plot } = geometry
  const gap = options.gap ?? 12
  const offsetX = options.offsetX ?? 22
  const verticalOffset = options.verticalOffset ?? 26
  const markerPad = options.markerPad ?? 24
  const vGap = Math.max(verticalOffset, markerPad, 12)
  const sideGap = Math.max(offsetX, 14)

  const plotBounds = {
    left: plot.left + gap,
    right: plot.right - gap,
    top: plot.top + gap,
    bottom: plot.bottom - gap
  }

  const plotHeight = plotBounds.bottom - plotBounds.top
  const inUpperHalf = center.y < plotBounds.top + plotHeight * 0.48

  const clearOfMarker = (rect) => !rectsOverlap(rect, keepOut)

  const rawCandidates = [
    { id: "below-right", top: keepOut.bottom + vGap, left: center.x + sideGap },
    { id: "below-left", top: keepOut.bottom + vGap, left: center.x - tooltipWidth - sideGap },
    { id: "right", top: center.y - tooltipHeight * 0.32, left: keepOut.right + sideGap },
    { id: "left", top: center.y - tooltipHeight * 0.32, left: keepOut.left - tooltipWidth - sideGap },
    { id: "above-right", top: keepOut.top - tooltipHeight - vGap, left: center.x + sideGap },
    { id: "above-left", top: keepOut.top - tooltipHeight - vGap, left: center.x - tooltipWidth - sideGap }
  ]

  const candidates = inUpperHalf
    ? rawCandidates.filter((candidate) => !candidate.id.startsWith("above"))
    : rawCandidates

  for (const candidate of candidates) {
    const left = clampHorizontal(candidate.left, tooltipWidth, plotBounds)
    const rect = placementRect(candidate.top, left, tooltipWidth, tooltipHeight)
    if (rectInsidePlot(rect, plotBounds) && clearOfMarker(rect)) {
      return { top: candidate.top, left }
    }
  }

  let left = clampHorizontal(center.x + sideGap, tooltipWidth, plotBounds)
  let top = keepOut.bottom + vGap

  if (!inUpperHalf && top + tooltipHeight > plotBounds.bottom) {
    top = keepOut.top - tooltipHeight - vGap
  }

  top = Math.max(plotBounds.top, Math.min(top, plotBounds.bottom - tooltipHeight))

  let rect = placementRect(top, left, tooltipWidth, tooltipHeight)
  if (rectsOverlap(rect, keepOut)) {
    left = clampHorizontal(keepOut.right + sideGap, tooltipWidth, plotBounds)
    top = Math.min(plotBounds.bottom - tooltipHeight, keepOut.bottom + vGap)
    rect = placementRect(top, left, tooltipWidth, tooltipHeight)
  }

  if (rectsOverlap(rect, keepOut) && inUpperHalf) {
    top = Math.min(plotBounds.bottom - tooltipHeight, keepOut.bottom + vGap)
    left = clampHorizontal(keepOut.right + sideGap, tooltipWidth, plotBounds)
  }

  return { top, left }
}

export function markTooltipPositioned(instance) {
  instance?.el?.querySelector(".apexcharts-tooltip")?.classList.add(POSITIONED_CLASS)
}

export function clearTooltipPositioned(instance) {
  instance?.el?.querySelector(".apexcharts-tooltip")?.classList.remove(POSITIONED_CLASS)
  if (instance?._lineTooltipLockRaf) {
    cancelAnimationFrame(instance._lineTooltipLockRaf)
    instance._lineTooltipLockRaf = null
  }
}

function applyTooltipPosition(tooltip, top, left) {
  tooltip.style.setProperty("left", `${left}px`, "important")
  tooltip.style.setProperty("top", `${top}px`, "important")
  tooltip.style.setProperty("right", "auto", "important")
  tooltip.style.setProperty("bottom", "auto", "important")
  tooltip.style.setProperty("transform", "none", "important")
}

function scheduleLineTooltipLock(instance, dataPointIndex, options) {
  if (instance._lineTooltipLockRaf) cancelAnimationFrame(instance._lineTooltipLockRaf)

  let frames = 0
  const tick = () => {
    const tooltip = instance.el?.querySelector(".apexcharts-tooltip.apexcharts-active")
    if (!tooltip || frames >= 12) {
      instance._lineTooltipLockRaf = null
      return
    }

    positionApexTooltip(instance, dataPointIndex, { ...options, _skipLock: true })
    frames += 1
    instance._lineTooltipLockRaf = requestAnimationFrame(tick)
  }

  instance._lineTooltipLockRaf = requestAnimationFrame(tick)
}

/**
 * Places the Apex tooltip near the hovered column without covering the data point.
 * Hides the tooltip until coordinates are set (see SCSS paired with POSITIONED_CLASS).
 */
export function positionApexTooltip(instance, dataPointIndex, options = {}) {
  const tooltip = instance?.el?.querySelector(".apexcharts-tooltip")
  if (!instance || !tooltip || dataPointIndex == null) return
  if (!tooltip.classList.contains("apexcharts-active")) return

  const globals = instance.w?.globals
  if (!globals) return

  const {
    mode = "line",
    offsetX = 20,
    gap = 12,
    barZoneTopFraction = 0.44,
    _skipLock = false
  } = options

  tooltip.style.visibility = "hidden"

  const tooltipWidth = Math.ceil(tooltip.getBoundingClientRect().width || tooltip.offsetWidth || 200)
  const measuredHeight = tooltip.getBoundingClientRect().height || tooltip.offsetHeight
  const tooltipHeight = Math.ceil(Math.max(measuredHeight, mode === "line" ? 68 : 76))

  let left
  let top

  if (mode === "composite") {
    const center = markerCenter(instance, dataPointIndex)
    if (!center) return

    const plotLeft = globals.translateX
    const plotRight = plotLeft + globals.gridWidth
    const plotTop = globals.translateY
    const plotBottom = plotTop + globals.gridHeight

    left = center.x + offsetX
    if (left + tooltipWidth > plotRight - gap) {
      left = center.x - tooltipWidth - offsetX
    }
    left = Math.max(plotLeft + gap, Math.min(left, plotRight - tooltipWidth - gap))
    const barZoneTop = plotTop + globals.gridHeight * barZoneTopFraction
    top = center.y - tooltipHeight / 2
    if (top + tooltipHeight > barZoneTop - gap) {
      top = barZoneTop - tooltipHeight - gap
    }
    top = Math.max(plotTop + gap, Math.min(top, plotBottom - tooltipHeight - gap))
  } else {
    let geometry = lineHoverGeometry(instance, dataPointIndex, tooltip, options)
    if (!geometry) {
      const center = markerCenter(instance, dataPointIndex)
      if (!center) {
        tooltip.style.removeProperty("visibility")
        return
      }

      geometry = {
        center,
        keepOut: {
          left: center.x - 12,
          right: center.x + 12,
          top: center.y - (options.dataLabelPad ?? 36),
          bottom: center.y + 12
        },
        plot: {
          left: globals.translateX,
          right: globals.translateX + globals.gridWidth,
          top: globals.translateY,
          bottom: globals.translateY + globals.gridHeight
        }
      }
    }

    const placed = computeLineTooltipPlacement(tooltipWidth, tooltipHeight, geometry, options)
    top = placed.top
    left = placed.left

    if (!_skipLock) scheduleLineTooltipLock(instance, dataPointIndex, options)
  }

  applyTooltipPosition(tooltip, top, left)
  tooltip.style.visibility = "visible"
  markTooltipPositioned(instance)
}
